import type { Language } from "../../../types/global.js";
import type { DinoTutorContextPatch } from "./dinoContextTopic.js";

type DinoPageKind =
    | "home"
    | "grammar"
    | "vocabulary"
    | "travel"
    | "journal"
    | "practice"
    | "daily"
    | "profile"
    | "shop"
    | "admin"
    | "auth"
    | "not-found";

interface DinoRouteContextInput {
    language: Language;
    pathname: string;
    search?: string;
}

/**
 * Converts public navigation state into non-sensitive tutor context. Route
 * state and arbitrary query values are never interpreted as learner data.
 */
function deriveDinoRouteContext({
    language,
    pathname
}: DinoRouteContextInput): DinoTutorContextPatch {
    const segments = getSafePathSegments(pathname);
    const pageKind = derivePageKind(segments);
    const identity = deriveRouteIdentity(pageKind, segments);

    return {
        language,
        route: {
            pathname
        },
        page: {
            kind: pageKind,
            ...identity.page
        },
        ...identity.context
    };
}

function derivePageKind(
    segments: readonly string[] | null
): DinoPageKind {
    if (!segments) {
        return "not-found";
    }

    const root = segments[0];

    if (!root) {
        return "home";
    }

    switch (root) {
        case "grammar":
        case "vocabulary":
        case "travel":
        case "journal":
        case "practice":
        case "daily":
        case "profile":
        case "shop":
            return root;
        case "archive":
            return "journal";
        case "placement":
            return "practice";
        case "saurus":
            return "profile";
        case "admin":
            return segments[1] === "content" ? "admin" : "not-found";
        case "auth":
        case "onboarding":
            return "auth";
        case "info":
            return isInstitutionalPage(segments[1]) ? "home" : "not-found";
        default:
            return "not-found";
    }
}

function deriveRouteIdentity(
    pageKind: DinoPageKind,
    segments: readonly string[] | null
): {
    page?: { id: string };
    context?: Pick<DinoTutorContextPatch, "content" | "lesson">;
} {
    if (!segments) {
        return {};
    }

    if (pageKind === "grammar" && segments[1] === "lesson" && segments[2]) {
        return {
            page: { id: segments[2] },
            context: {
                content: { id: segments[2], type: "grammar-lesson" },
                lesson: { id: segments[2] }
            }
        };
    }

    if (pageKind === "vocabulary" && segments[1] && segments[2]) {
        return {
            page: { id: segments[2] },
            context: {
                content: { id: segments[2], type: "vocabulary-pack" },
                lesson: { id: segments[2], level: segments[1] }
            }
        };
    }

    if (pageKind === "travel" && segments[1]) {
        return {
            page: { id: segments[1] },
            context: {
                content: { id: segments[1], type: "travel-lesson" },
                lesson: { id: segments[1] }
            }
        };
    }

    if (pageKind === "journal" && segments[0] === "journal" && segments[1]) {
        return {
            page: { id: segments[1] },
            context: {
                content: { id: segments[1], type: "journal-article" }
            }
        };
    }

    if (
        pageKind === "practice"
        && segments[0] === "practice"
        && segments[1]
        && segments[1] !== "review"
    ) {
        const activityId = segments[3] ?? segments[1];

        return {
            page: { id: activityId },
            context: {
                content: {
                    id: activityId,
                    type: segments[3] ? "practice-pack" : "practice-game"
                },
                ...(segments[2]
                    ? { lesson: { id: activityId, level: segments[2] } }
                    : {})
            }
        };
    }

    return {};
}

function getSafePathSegments(pathname: string): string[] | null {
    if (
        !pathname.startsWith("/")
        || pathname.startsWith("//")
        || pathname.includes("\\")
    ) {
        return null;
    }

    if (pathname === "/") {
        return [];
    }

    if (pathname.endsWith("/")) {
        return null;
    }

    const decoded: string[] = [];

    for (const segment of pathname.slice(1).split("/")) {
        try {
            const value = decodeURIComponent(segment);

            if (!isSafePublicId(value)) {
                return null;
            }

            decoded.push(value);
        } catch {
            return null;
        }
    }

    return decoded;
}

function isSafePublicId(value: string): boolean {
    return (
        value.length > 0
        && value.length <= 160
        && value.trim() === value
        && value !== "."
        && value !== ".."
        && !/[\\/\u0000-\u001f\u007f]/u.test(value)
    );
}

function isInstitutionalPage(value: string | undefined): boolean {
    return value === "about" || value === "contact" || value === "dino" || value === "work-with-us";
}

export { deriveDinoRouteContext };
export type { DinoPageKind, DinoRouteContextInput };
